import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { lastValueFrom, map } from 'rxjs';
import { BackendResponse } from '@fc-core/models/response.model';
import { ChatMessage } from '../models/chat-message';
import { Channel, ChannelConfig, JoinableChannel } from '../models/channel';

export enum ChannelType {
  GROUP = 'group',
  DIRECT = 'direct',
}

export interface ChannelPayload {
  name?: string;
  description?: string;
  type?: ChannelType;
  members: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ChatChannelsApiService {
  private url = environment.apiUrl + 'api/chat/channel/';
  private http = inject(HttpClient);

  getChannels(): Promise<Channel[]> {
    return lastValueFrom(
      this.http
        .get<BackendResponse<Channel[]>>(this.url)
        .pipe(map((res) => res.results)),
    );
  }

  async getJoinableChannels(): Promise<JoinableChannel[]> {
    const pageSize = 100;
    const channels: JoinableChannel[] = [];
    let next: string | null = `${this.url}joinable/?limit=${pageSize}`;
    while (next) {
      const page: BackendResponse<JoinableChannel[]> = await lastValueFrom(
        this.http.get<BackendResponse<JoinableChannel[]>>(next),
      );
      channels.push(...page.results);
      next = page.next;
    }
    return channels;
  }

  getChannelById(channelId: string): Promise<Channel> {
    return lastValueFrom(this.http.get<Channel>(`${this.url}${channelId}/`));
  }

  joinChannel(channelId: string): Promise<Channel> {
    return lastValueFrom(
      this.http.put<Channel>(`${this.url}${channelId}/join/`, {}),
    );
  }

  leaveChannel(channelId: string): Promise<Channel> {
    return lastValueFrom(
      this.http.put<Channel>(`${this.url}${channelId}/leave/`, {}),
    );
  }

  updateChannel(channel: Channel): Promise<Channel> {
    return lastValueFrom(
      this.http.put<Channel>(`${this.url}${channel.id}/`, channel),
    );
  }

  createChannel(channelPayload: ChannelPayload): Promise<Channel> {
    return lastValueFrom(this.http.post<Channel>(this.url, channelPayload));
  }

  getChannelMessages(channelId: string): Promise<ChatMessage[]> {
    return lastValueFrom(
      this.http
        .get<
          BackendResponse<ChatMessage[]>
        >(`${this.url}${channelId}/messages/`)
        .pipe(map((res) => res.results)),
    );
  }

  deleteChannel(channelId: string): Promise<void> {
    return lastValueFrom(this.http.delete<void>(`${this.url}${channelId}/`));
  }

  updateChannelConfig(
    channelConfigId: string,
    config: Partial<ChannelConfig>,
  ): Promise<ChannelConfig> {
    return lastValueFrom(
      this.http.patch<ChannelConfig>(
        `${this.url}channel-config/${channelConfigId}/`,
        config,
      ),
    );
  }
}
